import {
  AssetRequestStatus,
  AssetRequestType,
  Column,
  ColumnType,
  DescriptionOption,
} from '@atom/types/assetRequest';

export const INITIAL_PAGE = 1;
export const INITIAL_LIMIT = 25;
export const INITIAL_SORT_BY = 'name';
export const INITIAL_IS_ASCENDING = false;

export type InitialSort = {
  sortBy: string;
  isAscending: boolean;
};

export const DEFAULT_INITIAL_SORT: InitialSort = {
  sortBy: INITIAL_SORT_BY,
  isAscending: INITIAL_IS_ASCENDING,
};

export const TYPE_LABELS = {
  [AssetRequestType.SDDOT_STOCKPILE_TRANSFER]: 'Stockpile Transfer',
  [AssetRequestType.SDDOT_INVENTORY_TRANSFER]: 'Inventory Transfer',
  [AssetRequestType.SDDOT_REPLENISHMENT]: 'Replenishment',
  [AssetRequestType.SDDOT_ADJUSTMENT]: 'Adjustment',
  [AssetRequestType.ARDOT_118_TRANSFER]: '118 Process',
  [AssetRequestType.ADOT_INVENTORY_TRANSFER]: 'Inventory Transfer',
  [AssetRequestType.ADOT_REPLENISHMENT]: 'Replenishment',
  [AssetRequestType.ADOT_ADJUSTMENT]: 'Adjustment',
};

export const STATUS_LABELS = {
  [AssetRequestStatus.FULLY_RECEIVED]: 'Fully Received',
  [AssetRequestStatus.PARTIALLY_RECEIVED]: 'Partially Received',
  [AssetRequestStatus.PENDING]: 'Pending',
  [AssetRequestStatus.CANCELED]: 'Canceled',
  [AssetRequestStatus.REJECTED]: 'Rejected',
  [AssetRequestStatus.FULL_APPROVAL]: 'Fully Approved',
  [AssetRequestStatus.PARTIAL_APPROVAL]: 'Partially Approved',
  [AssetRequestStatus.PENDING_APPROVAL]: 'Pending Approval',
  [AssetRequestStatus.PENDING_REVIEW]: 'Pending Review',
  [AssetRequestStatus.APPROVED]: 'Approved',
};

export const STATUS_COLORS = {
  [AssetRequestStatus.FULLY_RECEIVED]: '#3FA142',
  [AssetRequestStatus.PARTIALLY_RECEIVED]: '#FF8F00',
  [AssetRequestStatus.PENDING]: '#FF8F00',
  [AssetRequestStatus.CANCELED]: '#999',
  [AssetRequestStatus.REJECTED]: '#ED3620',
  [AssetRequestStatus.FULL_APPROVAL]: '#3FA142',
  [AssetRequestStatus.PARTIAL_APPROVAL]: '#00A89A',
  [AssetRequestStatus.PENDING_APPROVAL]: '#FF8F00',
  [AssetRequestStatus.PENDING_REVIEW]: '#9209AE',
  [AssetRequestStatus.APPROVED]: '#3FA142',
};

export const DESCRIPTION_LABELS = {
  [DescriptionOption.ALL]: 'All',
  [DescriptionOption.OUTGOING]: 'Outgoing',
  [DescriptionOption.INCOMING]: 'Incoming',
};

export const DESCRIPTION_OPTIONS = [
  DescriptionOption.ALL,
  DescriptionOption.OUTGOING,
  DescriptionOption.INCOMING,
];

// ARDOT Constants
export const ARDOT_118_STATUS_OPTIONS = [
  AssetRequestStatus.PENDING_REVIEW,
  AssetRequestStatus.PENDING_APPROVAL,
  AssetRequestStatus.CANCELED,
  AssetRequestStatus.REJECTED,
  AssetRequestStatus.APPROVED,
];

export const ardotColumns = (isPortalView: boolean): Column[] => {
  return [
    {
      type: ColumnType.NAME,
      label: 'Request ID',
      sortValue: 'name',
    },
    ...(isPortalView
      ? [
          {
            type: ColumnType.ASSET_NAME,
            label: 'Inventory Name',
          },
          {
            type: ColumnType.SCHEMA_NAME,
            label: 'Inventory Type',
          },
        ]
      : []),
    {
      type: ColumnType.STATUS,
      label: 'Status',
    },
    {
      type: ColumnType.CATEGORY_PATH,
      label: 'From District or Division',
    },
    {
      type: ColumnType.TO_CATEGORY_PATH,
      label: 'To District or Division',
    },
    {
      type: ColumnType.REVIEWER,
      label: 'Reviewer',
    },
    {
      type: ColumnType.RECEIVER,
      label: 'Receiver',
    },
    {
      type: ColumnType.RESOLUTION_DATE,
      label: 'Resolution Date',
      sortValue: 'resolutionDate',
    },
    {
      type: ColumnType.CREATED_DATE,
      label: 'Created Date',
      sortValue: 'createdDate',
    },
    {
      type: ColumnType.CREATED_BY,
      label: 'Created By',
    },
  ];
};

export const ARDOT_CONFIRM_MODAL_CONTENT = {
  title: `Create Transfer Request?`,
  content:
    'Once the request is created, it requires approval from both the reviewer and the receiver before the transfer can be completed.',
};

// SDDOT Constants
export const SDDOT_STOCKPILE_SCHEMA_ID = '676ec09039095020a61ed979';
export const SDDOT_INVENTORY_SCHEMA_ID = '676d8107400a294a5634787c';
export const SDDOT_FUEL_SCHEMA_ID = '6736246a730f2a1d9dfc4086';
export const ITEM_CODE_ATTRIBUTE_NAME = 'Item Code';

export const INVENTORY_STATUS_OPTIONS = [
  AssetRequestStatus.PENDING,
  AssetRequestStatus.CANCELED,
  AssetRequestStatus.REJECTED,
  AssetRequestStatus.FULL_APPROVAL,
  AssetRequestStatus.PARTIAL_APPROVAL,
];

export const REPLENISHMENT_STATUS_OPTIONS = [
  AssetRequestStatus.FULLY_RECEIVED,
  AssetRequestStatus.PARTIALLY_RECEIVED,
];

export const sddotColumns = (
  type: AssetRequestType,
  isPortalView: boolean,
): Column[] => {
  const columns = {
    [AssetRequestType.SDDOT_STOCKPILE_TRANSFER]: [
      {
        type: ColumnType.NAME,
        label: 'Request ID',
        sortValue: 'name',
      },
      ...(isPortalView
        ? [
            {
              type: ColumnType.FROM_ASSET_NAME,
              label: 'Transfer From',
            },
            {
              type: ColumnType.ASSET_NAME,
              label: 'Transfer To',
            },
          ]
        : []),
      ...(!isPortalView
        ? [
            {
              type: ColumnType.DESCRIPTION,
              label: 'Description',
            },
          ]
        : []),
      {
        type: ColumnType.QUANTITY,
        label: 'Quantity',
      },
      {
        type: ColumnType.CREATED_BY,
        label: 'Created By',
      },
      {
        type: ColumnType.CREATED_DATE,
        label: 'Created Date',
        sortValue: 'createdDate',
      },
    ],
    [AssetRequestType.SDDOT_INVENTORY_TRANSFER]: [
      {
        type: ColumnType.NAME,
        label: 'Request ID',
        sortValue: 'name',
      },
      ...(isPortalView
        ? [
            {
              type: ColumnType.FROM_ASSET_NAME,
              label: 'Transfer From',
            },
            {
              type: ColumnType.ASSET_NAME,
              label: 'Transfer To',
            },
          ]
        : []),
      {
        type: ColumnType.STATUS,
        label: 'Status',
      },
      ...(!isPortalView
        ? [
            {
              type: ColumnType.DESCRIPTION,
              label: 'Description',
            },
          ]
        : []),
      {
        type: ColumnType.QUANTITY_ORDERED,
        label: 'Quantity',
      },
      {
        type: ColumnType.ASSIGNED_TO,
        label: 'Assigned To',
      },
      {
        type: ColumnType.CREATED_BY,
        label: 'Created By',
      },
      {
        type: ColumnType.CREATED_DATE,
        label: 'Created Date',
        sortValue: 'createdDate',
      },
      {
        type: ColumnType.UPDATED_DATE,
        label: 'Resolution Date',
        sortValue: 'updatedDate',
      },
    ],
    [AssetRequestType.SDDOT_REPLENISHMENT]: [
      {
        type: ColumnType.NAME,
        label: 'Request ID',
        sortValue: 'name',
      },
      ...(isPortalView
        ? [
            {
              type: ColumnType.ASSET_NAME,
              label: 'Inventory Name',
            },
          ]
        : []),
      {
        type: ColumnType.STATUS,
        label: 'Status',
      },
      {
        type: ColumnType.REPLENISHMENTS,
        label: 'Replenishments',
      },
      {
        type: ColumnType.QUANTITY_ORDERED,
        label: 'Quantity Ordered',
      },
      {
        type: ColumnType.QUANTITY_RECEIVED,
        label: 'Quantity Received',
      },
      {
        type: ColumnType.CREATED_BY,
        label: 'Created By',
      },
      {
        type: ColumnType.CREATED_DATE,
        label: 'Created Date',
        sortValue: 'createdDate',
      },
    ],
    [AssetRequestType.SDDOT_ADJUSTMENT]: [
      {
        type: ColumnType.NAME,
        label: 'Request ID',
        sortValue: 'name',
      },
      ...(isPortalView
        ? [
            {
              type: ColumnType.ASSET_NAME,
              label: 'Inventory Name',
            },
          ]
        : []),
      {
        type: ColumnType.QUANTITY,
        label: 'Quantity',
      },
      {
        type: ColumnType.CREATED_BY,
        label: 'Created By',
      },
      {
        type: ColumnType.CREATED_DATE,
        label: 'Created Date',
        sortValue: 'createdDate',
      },
    ],
  };

  return columns[type] || [];
};

export const SDDOT_CONFIRM_MODAL_CONTENT = {
  [AssetRequestType.SDDOT_STOCKPILE_TRANSFER]: {
    title: `Create Request: ${
      TYPE_LABELS[AssetRequestType.SDDOT_STOCKPILE_TRANSFER]
    }?`,
    content:
      'Once the request is created, the inventory stock will be updated immediately.',
  },
  [AssetRequestType.SDDOT_INVENTORY_TRANSFER]: {
    title: `Create Request: ${
      TYPE_LABELS[AssetRequestType.SDDOT_INVENTORY_TRANSFER]
    }?`,
    content:
      'Once the request is created, it will be marked as pending and will need another user’s approval before the stock is updated.',
  },
  [AssetRequestType.SDDOT_REPLENISHMENT]: {
    title: `Create Request: ${
      TYPE_LABELS[AssetRequestType.SDDOT_REPLENISHMENT]
    }?`,
    content:
      'Once the request is created, the inventory stock will be updated immediately.',
  },
  [AssetRequestType.SDDOT_ADJUSTMENT]: {
    title: `Create Request: ${TYPE_LABELS[AssetRequestType.SDDOT_ADJUSTMENT]}?`,
    content:
      'Once the request is created, the inventory stock will be updated immediately.',
  },
};

export const ADJUSTMENT_REASON_OPTIONS = [
  '750CRDT - Credits',
  '750CYCL - Random Count',
  '750FUEL - Fuel',
  '750SALE - Assets to Annual Sales',
  '750SIGN - Signage',
  '750STKP - Stockpile',
  '750WHSE - Annual Inventory',
];

// ADOT Constants
export const ADOT_INITIAL_SORT: InitialSort = {
  sortBy: 'createdDate',
  isAscending: false,
};

export const ADOT_REQUEST_TYPES = [
  AssetRequestType.ADOT_INVENTORY_TRANSFER,
  AssetRequestType.ADOT_REPLENISHMENT,
  AssetRequestType.ADOT_ADJUSTMENT,
];

export const ADOT_ADJUSTMENT_REASON_OPTIONS = [
  'Annual Inventory',
  'Random Count',
  'Accident',
];

export const ADOT_CONFIRM_MODAL_CONTENT = {
  [AssetRequestType.ADOT_INVENTORY_TRANSFER]: {
    title: `Create Request: ${
      TYPE_LABELS[AssetRequestType.ADOT_INVENTORY_TRANSFER]
    }?`,
    content:
      'Once the request is created, it will be marked as pending and will need approval before the stock is updated.',
  },
  [AssetRequestType.ADOT_ADJUSTMENT]: {
    title: `Create Request: ${TYPE_LABELS[AssetRequestType.ADOT_ADJUSTMENT]}?`,
    content:
      "Once the request is created, the item's Total Remaining will be updated immediately.",
  },
  [AssetRequestType.ADOT_REPLENISHMENT]: {
    title: `Create Request: ${
      TYPE_LABELS[AssetRequestType.ADOT_REPLENISHMENT]
    }?`,
    content:
      "Once the request is created, the item's Total Remaining and Unit Cost will be updated immediately.",
  },
};

export const ADOT_UPDATE_SNACKBAR_MESSAGES = {
  [AssetRequestStatus.FULL_APPROVAL]: 'Request approved.',
  [AssetRequestStatus.PARTIAL_APPROVAL]: 'Request approved.',
  [AssetRequestStatus.REJECTED]: 'Request rejected.',
  [AssetRequestStatus.CANCELED]: 'Request canceled.',
};

export const ADOT_UPDATE_FORBIDDEN_MESSAGE =
  'You are no longer assigned to this request.';

export const adotColumns = (
  type: AssetRequestType,
  isPortalView: boolean,
): Column[] => {
  const columns = {
    [AssetRequestType.ADOT_INVENTORY_TRANSFER]: [
      {
        type: ColumnType.NAME,
        label: 'Request ID',
        sortValue: 'name',
      },
      ...(isPortalView
        ? [
            {
              type: ColumnType.FROM_ASSET_NAME,
              label: 'Transfer From',
            },
            {
              type: ColumnType.ASSET_NAME,
              label: 'Transfer To',
              sortValue: 'assetName',
            },
          ]
        : []),
      {
        type: ColumnType.STATUS,
        label: 'Status',
        ...(isPortalView && { sortValue: 'status' }),
      },
      ...(!isPortalView
        ? [
            {
              type: ColumnType.DESCRIPTION,
              label: 'Description',
            },
          ]
        : []),
      {
        type: ColumnType.QUANTITY_ORDERED,
        label: 'Quantity',
      },
      {
        type: ColumnType.ASSIGNEES,
        label: 'Assigned To',
      },
      {
        type: ColumnType.CREATED_BY,
        label: 'Created By',
      },
      {
        type: ColumnType.CREATED_DATE,
        label: 'Created Date',
        sortValue: 'createdDate',
      },
      {
        type: ColumnType.RESOLUTION_DATE,
        label: 'Resolution Date',
        sortValue: 'resolutionDate',
      },
    ],
    [AssetRequestType.ADOT_REPLENISHMENT]: [
      {
        type: ColumnType.NAME,
        label: 'Request ID',
        sortValue: 'name',
      },
      ...(isPortalView
        ? [
            {
              type: ColumnType.ASSET_NAME,
              label: 'Inventory Name',
              sortValue: 'assetName',
            },
          ]
        : []),
      {
        type: ColumnType.STATUS,
        label: 'Status',
        ...(isPortalView && { sortValue: 'status' }),
      },
      {
        type: ColumnType.REPLENISHMENTS,
        label: 'Replenishments',
      },
      {
        type: ColumnType.QUANTITY_ORDERED,
        label: 'Quantity Ordered',
      },
      {
        type: ColumnType.QUANTITY_RECEIVED,
        label: 'Quantity Received',
      },
      {
        type: ColumnType.CREATED_BY,
        label: 'Created By',
      },
      {
        type: ColumnType.CREATED_DATE,
        label: 'Created Date',
        sortValue: 'createdDate',
      },
    ],
    [AssetRequestType.ADOT_ADJUSTMENT]: [
      {
        type: ColumnType.NAME,
        label: 'Request ID',
        sortValue: 'name',
      },
      ...(isPortalView
        ? [
            {
              type: ColumnType.ASSET_NAME,
              label: 'Inventory Name',
              sortValue: 'assetName',
            },
          ]
        : []),
      {
        type: ColumnType.QUANTITY,
        label: 'Quantity',
      },
      {
        type: ColumnType.CREATED_BY,
        label: 'Created By',
      },
      {
        type: ColumnType.CREATED_DATE,
        label: 'Created Date',
        sortValue: 'createdDate',
      },
    ],
  };

  return columns[type] || [];
};
